// shared/analytics/trackerCore.ts
/**
 * Pure tracker logic — storage and clock are injected so every rule is unit-tested without a DOM.
 * Browser-only state (spec §1.2): sessionStorage lm_s {id,last}; localStorage lm_ft (first touch,
 * written once), lm_lt (latest non-direct session), lm_seen (a DATE, never an id). Notes expire
 * after 90 days. Nothing here is ever sent with pageviews — only with enquire/call/book requests.
 */
import { classifyChannel, sourceOf, type Touch, type Utm } from './channel';
import type { ClickKind } from './enums';

export interface KV { getItem(k: string): string | null; setItem(k: string, v: string): void }
export interface Landing { referrerHost: string | null; utm: Utm | null; clickKind: ClickKind | null; template: string }
export type AttributionNoteOut = { ft?: Touch; lt?: Touch };

export const SESSION_IDLE_MS = 30 * 60_000;
export const NOTE_TTL_DAYS = 90;
const UTM_KEYS: ReadonlyArray<[keyof Utm, string]> = [
  ['s', 'utm_source'], ['m', 'utm_medium'], ['c', 'utm_campaign'], ['ct', 'utm_content'], ['t', 'utm_term'],
];

export function localDay(d: Date): string {
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function readUtm(search: string): { utm: Utm | null; clickKind: ClickKind | null } {
  let q: URLSearchParams;
  try { q = new URLSearchParams(search); } catch { return { utm: null, clickKind: null }; }
  const utm: Utm = {};
  for (const [k, name] of UTM_KEYS) {
    const v = (q.get(name) ?? '').trim();
    if (v) utm[k] = v.slice(0, 80);
  }
  const clickKind: ClickKind | null = q.has('gclid') ? 'gclid' : q.has('fbclid') ? 'fbclid' : null;
  return { utm: Object.keys(utm).length ? utm : null, clickKind };
}

export function referrerHostOf(referrer: string): string | null {
  if (!referrer) return null;
  try { return new URL(referrer).hostname.toLowerCase() || null; } catch { return null; }
}

function readJson<T>(kv: KV, key: string): T | null {
  try { const s = kv.getItem(key); return s ? (JSON.parse(s) as T) : null; } catch { return null; }
}
function writeJson(kv: KV, key: string, v: unknown): void {
  try { kv.setItem(key, JSON.stringify(v)); } catch { /* quota / private mode */ }
}

export function touchSession(ss: KV, nowMs: number, rand: () => string): { id: string; isNew: boolean } {
  const cur = readJson<{ id?: unknown; last?: unknown }>(ss, 'lm_s');
  const fresh = !cur || typeof cur.id !== 'string' || typeof cur.last !== 'number' || nowMs - cur.last > SESSION_IDLE_MS;
  const id = fresh ? rand() : (cur!.id as string);
  writeJson(ss, 'lm_s', { id, last: nowMs });
  return { id, isNew: fresh };
}

export function buildTouch(l: Landing, now: Date): Touch {
  const input = { referrerHost: l.referrerHost, utm: l.utm, clickKind: l.clickKind };
  const t: Touch = { ch: classifyChannel(input), lp: (l.template || '/').slice(0, 120), d: localDay(now) };
  const src = sourceOf(input);
  if (src) t.src = src.slice(0, 120);
  if (l.utm?.m) t.med = l.utm.m.slice(0, 120);
  if (l.utm?.c) t.cmp = l.utm.c.slice(0, 120);
  return t;
}

function expired(t: Touch | null, now: Date): boolean {
  if (!t || typeof t.d !== 'string') return true;
  const then = Date.parse(`${t.d}T00:00:00`);
  return !Number.isFinite(then) || now.getTime() - then > NOTE_TTL_DAYS * 86_400_000;
}

export function updateNotes(ls: KV, l: Landing, isNewSession: boolean, now: Date): void {
  const touch = buildTouch(l, now);
  if (expired(readJson<Touch>(ls, 'lm_ft'), now)) writeJson(ls, 'lm_ft', touch);
  if (isNewSession && touch.ch !== 'direct' && touch.ch !== 'internal') writeJson(ls, 'lm_lt', touch);
}

export function readNote(ls: KV, now: Date): AttributionNoteOut | undefined {
  const ft = readJson<Touch>(ls, 'lm_ft');
  const lt = readJson<Touch>(ls, 'lm_lt');
  const out: AttributionNoteOut = {};
  if (ft && !expired(ft, now)) out.ft = ft;
  if (lt && !expired(lt, now)) out.lt = lt;
  return out.ft || out.lt ? out : undefined;
}

export function markSeen(ls: KV, now: Date): boolean {
  try {
    const seen = !!ls.getItem('lm_seen');
    ls.setItem('lm_seen', localDay(now));
    return seen;
  } catch {
    return false;
  }
}

export function screenBucket(width: number): 's' | 'm' | 'l' {
  return width < 600 ? 's' : width < 1024 ? 'm' : 'l';
}

export function isOptedOut(nav?: { globalPrivacyControl?: unknown; doNotTrack?: unknown }, win?: { doNotTrack?: unknown }): boolean {
  return nav?.globalPrivacyControl === true || nav?.doNotTrack === '1' || win?.doNotTrack === '1';
}

export function clickEventFor(href: string): 'whatsapp_clicked' | 'tel_clicked' | null {
  const h = href.trim().toLowerCase();
  if (h.startsWith('tel:')) return 'tel_clicked';
  if (/wa\.me|whatsapp/.test(h)) return 'whatsapp_clicked';
  return null;
}
